import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { LEAVE_STATUS, LEAVE_TYPES } from '../../lib/leaveLabels';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');
const FILTERS = [['pending', 'En attente'], ['approved', 'Approuvées'], ['rejected', 'Refusées'], ['', 'Toutes']];

const DecisionDialog = ({ ctx, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(''); }, [ctx]);
  const ok = ctx?.decision === 'approved';
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/leave-requests/${ctx.req.id}/${ok ? 'approve' : 'reject'}`, { manager_note: note });
      toast.success(ok ? 'Demande approuvée' : 'Demande refusée');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="staff-decision-dialog">
        <DialogHeader>
          <DialogTitle className={ok ? 'text-emerald-700' : 'text-rose-700'}>{ok ? 'Approuver la demande' : 'Refuser la demande'}</DialogTitle>
          <DialogDescription>{ctx && <><b>{ctx.req.user_name}</b> — {LEAVE_TYPES[ctx.req.request_type]} du {fmt(ctx.req.start_date)}</>}</DialogDescription>
        </DialogHeader>
        <Label className="text-xs font-semibold">Note au collaborateur (optionnelle)</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="staff-decision-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button disabled={busy} onClick={submit} className={ok ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'} data-testid="staff-decision-confirm">Confirmer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RequestRow = ({ r, onDecide }) => {
  const S = LEAVE_STATUS[r.status] || LEAVE_STATUS.pending;
  const Icon = S.icon;
  return (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 border border-[#B85C38]/20 rounded-lg p-3 hover:bg-[#F5EFE1]/30" data-testid={`staff-request-${r.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <User className="w-4 h-4 text-[#B85C38]" /><b className="text-[#2B3A2C]">{r.user_name || '—'}</b>
          <Badge variant="outline">{LEAVE_TYPES[r.request_type] || r.request_type}</Badge>
          <Badge className={`border ${S.className} flex items-center gap-1`}><Icon className="w-3 h-3" />{S.label}</Badge>
        </div>
        <p className="text-sm text-gray-700 mt-1">Du <b>{fmt(r.start_date)}</b>{r.end_date && r.end_date !== r.start_date && <> au <b>{fmt(r.end_date)}</b></>} · <span className="text-xs text-gray-500">{r.days_count} jour(s)</span></p>
        {r.reason && <p className="text-xs text-gray-600 mt-1 italic break-words">« {r.reason} »</p>}
        {r.decided_by_name && <p className="text-[11px] text-gray-500 mt-1">Décidée par {r.decided_by_name} le {fmt(r.decided_at)}{r.manager_note && <> · « {r.manager_note} »</>}</p>}
      </div>
      {r.status === 'pending' && (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" onClick={() => onDecide(r, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`staff-approve-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approuver</Button>
          <Button size="sm" variant="outline" onClick={() => onDecide(r, 'rejected')} className="border-rose-400 text-rose-700 hover:bg-rose-50" data-testid={`staff-reject-${r.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />Refuser</Button>
        </div>
      )}
    </div>
  );
};

export const StaffRequestsTab = ({ onCountChange }) => {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/leave-requests', { params: status ? { status } : {} });
      setRows(r.data || []);
      if (status === 'pending') onCountChange?.(r.data?.length || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de chargement'); } finally { setLoading(false); }
  }, [status, onCountChange]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="staff-requests-tab">
      <CardHeader>
        <CardTitle>Demandes du personnel</CardTitle>
        <CardDescription>Congés, absences, journées en avance et autres demandes des collaborateurs.</CardDescription>
        <div className="flex flex-wrap gap-2 pt-2">
          {FILTERS.map(([k, l]) => (
            <button key={k || 'all'} type="button" onClick={() => setStatus(k)} data-testid={`staff-filter-${k || 'all'}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${status === k ? 'bg-[#2B3A2C] text-white' : 'bg-[#F5EFE1] text-[#2B3A2C] hover:bg-[#EADFC8]'}`}>{l}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" /></div>
          : rows.length === 0 ? <p className="text-sm text-gray-500 text-center py-6" data-testid="staff-requests-empty">Aucune demande.</p>
          : rows.map((r) => <RequestRow key={r.id} r={r} onDecide={(req, decision) => setCtx({ req, decision })} />)}
      </CardContent>
      <DecisionDialog ctx={ctx} onClose={() => setCtx(null)} onDone={() => { setCtx(null); load(); }} />
    </Card>
  );
};

export default StaffRequestsTab;
